import { valibotResolver } from "@hookform/resolvers/valibot";
import { type JSX, useEffect, useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

import { TEMPLATE_TYPES } from "features/templates/constants";
import useTemplates from "features/templates/datahooks/useTemplates";
import type { TemplateSchemaType } from "features/templates/schemas";
import type { CreatedTemplate, TemplateType } from "features/templates/types";
import {
  getEmptyTemplate,
  getTemplateFromCreatedTemplate,
} from "features/templates/utils";

import useTemplateMutations from "../../../pages/templates/datahooks/useTemplateMutations";

type CreateOrEditTemplateProps = {
  children: (
    onSubmit: () => Promise<void>,
    isLoading: boolean,
    form: UseFormReturn<TemplateSchemaType>,
  ) => JSX.Element;
} & (
  | {
      initialData: CreatedTemplate;
      onSuccess: () => void;
    }
  | {
      initialData: TemplateType;
      onSuccess: (createdTemplate: CreatedTemplate) => void;
    }
);

export default function CreateOrEditTemplate({
  initialData,
  onSuccess,
  children,
}: CreateOrEditTemplateProps) {
  const prevTemplateIdRef = useRef<string>();
  const isNewTemplate = typeof initialData === "string";

  const { validationSchema } =
    TEMPLATE_TYPES[isNewTemplate ? initialData : initialData.type];

  const {
    createTemplate,
    updateTemplate,
    isUpdatingTemplate,
    isCreatingTemplate,
  } = useTemplateMutations();

  const { allTemplates } = useTemplates();

  let defaultValues: TemplateSchemaType;
  if (isNewTemplate) {
    const sessionStorageItem = sessionStorage.getItem(initialData);
    defaultValues = sessionStorageItem
      ? JSON.parse(sessionStorageItem)
      : getEmptyTemplate(initialData);
  } else {
    defaultValues = getTemplateFromCreatedTemplate(initialData);
  }
  const form = useForm<TemplateSchemaType>({
    defaultValues,
    resolver: valibotResolver(validationSchema),
  });
  const { handleSubmit, setError, watch } = form;

  useEffect(() => {
    if (isNewTemplate) {
      const subscription = watch((value) => {
        sessionStorage.setItem(value.type, JSON.stringify(value));
      });
      return () => subscription.unsubscribe();
    }
  }, [watch, isNewTemplate]);

  useEffect(() => {
    // because name changes are saved separately on desktop, we have to ensure that template doesn't reset when only the name is changed
    // issue was reported here https://linear.app/constel/issue/ENG-6264/renaming-voice-template-before-saving-new-recording-discards-the-newly
    if (isNewTemplate || prevTemplateIdRef.current !== initialData.id) {
      form.reset(defaultValues);
    } else {
      form.reset(defaultValues, { keepDirtyValues: true, keepDirty: true });
    }
    if (!isNewTemplate) {
      prevTemplateIdRef.current = initialData.id;
    }
  }, [initialData]);

  async function onSubmit(data: TemplateSchemaType) {
    if (
      (isNewTemplate || data.name !== initialData.name) &&
      allTemplates.find(({ name }) => name === data.name.trim())
    ) {
      setError("name", { message: "Template name already exists" });
    } else if (isNewTemplate) {
      const createdTemplate = await createTemplate({
        template: {
          type: initialData,
          ...data,
        },
      });
      onSuccess(createdTemplate);
      sessionStorage.removeItem(initialData);
    } else {
      await updateTemplate({
        templateId: initialData.id,
        updates: data,
      });
      onSuccess();
    }
  }
  return children(
    handleSubmit(onSubmit),
    isCreatingTemplate || isUpdatingTemplate,
    form,
  );
}
